<script setup lang="ts">
import { ref, onMounted, reactive } from "vue";
import { bemBuilder } from "@chatfood/core-utils";
import {
  AtomText,
  SatSelect,
  AtomButton,
  AtomInput,
  SatFormControl,
  AtomInputMoney,
} from "@chatfood/design-system";
import type { ISatSelectProps } from "@chatfood/design-system";
import { useLocale } from "~/composables/use-locale";
import { useBasketStore } from "~/stores/basket";
import { useGiftCardStore } from "~/stores/giftCard";

type ISelectOptions = ISatSelectProps["options"][0];

const emits = defineEmits<{
  close: [MouseEvent];
}>();
const css = bemBuilder("apply-gift-card");
const { t } = useLocale();
const basket = useBasketStore();
const giftCardStore = useGiftCardStore();
const isApplyingGiftCard = ref(false);
const showFormError = ref(false);

type IGiftCard = {
  provider: {
    id: string;
    name: string;
  };
  code: string;
  pin?: string;
  amount?: {
    value: number;
    currency: string;
  };
};

const giftCard: IGiftCard = reactive({
  provider: {
    id: "",
    name: "",
  },
  code: "",
  pin: undefined,
  amount: undefined,
});

const onSelectProvider = (provider: ISelectOptions) => {
  giftCard.provider = provider;
};

onMounted(() => {
  if (giftCardStore.providers.length === 1) {
    giftCard.provider = giftCardStore.providers[0];
  }
});

const apply = async (event: MouseEvent) => {
  isApplyingGiftCard.value = true;

  if (!giftCard.amount) {
    return;
  }

  try {
    await basket.applyGiftCard({
      cardNumber: giftCard.code,
      amount: giftCard.amount.value,
      pin: giftCard.pin || undefined,
      providerId: giftCard.provider.id,
    });

    emits("close", event);
  } catch {
    showFormError.value = true;
  } finally {
    isApplyingGiftCard.value = false;
  }
};
</script>

<template>
  <div :class="css()">
    <AtomText type="h3" color="color-text-headline">
      {{ t("components.apply_gift_card.title") }}
    </AtomText>
    <div :class="css('form')">
      <SatSelect
        v-if="giftCardStore.providers.length > 1"
        :options="giftCardStore.providers"
        size="large"
        data-test="provider-selector"
        :placeholder="
          t('components.apply_gift_card.select_provider_placeholder')
        "
        :has-errors="false"
        :disabled="false"
        :append-to-body="true"
        :value="giftCard.provider"
        @select="onSelectProvider"
      />
      <SatFormControl>
        <AtomInput
          :value="giftCard.code"
          data-test="code"
          :placeholder="t('components.apply_gift_card.code_placeholder')"
          @keyup="(value) => (giftCard.code = value)"
        />
      </SatFormControl>
      <div :class="css('amount-block')">
        <SatFormControl :class="css('form-control')">
          <AtomInputMoney
            :has-error="false"
            :disabled="false"
            :value="{
              value: giftCard.amount?.value || 0,
              currency: basket.currency,
            }"
            @input="(value) => (giftCard.amount = value)"
          />
        </SatFormControl>
        <SatFormControl :class="css('form-control')">
          <AtomInput
            :value="giftCard.pin"
            data-test="pin"
            :placeholder="t('components.apply_gift_card.pin_placeholder')"
            @keyup="(value) => (giftCard.pin = value)"
          />
        </SatFormControl>
      </div>
    </div>
    <AtomText
      v-if="showFormError"
      data-test="error-message"
      color="color-error"
      type="body"
    >
      {{ t("components.apply_gift_card.error_message") }}
    </AtomText>
    <AtomButton
      size="large"
      variant="primary"
      :is-loading="isApplyingGiftCard"
      theme="filled"
      data-test="apply-cta"
      :is-disabled="!Boolean(giftCard.amount?.value) || !Boolean(giftCard.code)"
      @click.prevent="apply($event)"
    >
      {{ t("components.apply_gift_card.apply_cta") }}
    </AtomButton>
  </div>
</template>

<style lang="scss" scoped>
.apply-gift-card {
  padding: var(--spacing-lg);
  display: flex;
  flex-direction: column;
  gap: var(--spacing-lg);

  &__loading {
    margin: 0 auto;
  }

  &__form {
    display: flex;
    gap: var(--spacing-md);
    flex-direction: column;
  }

  &__amount-block {
    display: flex;
    gap: var(--spacing-md);
  }

  &__form-control {
    width: 100%;
  }
}
</style>
